import React, { useState, useEffect, useRef } from "react";
import {
  IndianRupee,
  Receipt,
  Wallet,
  ChevronLeft,
  ChevronRight,
  Users,
  Percent,
  Tag,
  TrendingUp,
  AlertCircle,
  Clock,
  Download,
  Filter,
} from "lucide-react";
import "react-datepicker/dist/react-datepicker.css";
import "../../assets/styles/datepicker.css";
import { useCloud } from "../../hooks/useCloud";
import { SUCCESS, REPORT_DATE_TYPES } from "../../assets/constants";
import { GenericPieChart } from "../Charts/GenericPieChart";
import { GenericBarChart } from "../Charts/GenericBarChart";
import { StackedProgressBar } from "../StackedProgressBar";
import DateFilter from "../DateFilter";
import OrderStats from "../OrderStats";
import { observer } from "mobx-react-lite";
import dateStore from "../../hooks/useDate";
import ReportLoadingState from "./LoadingState";
import { DataTable } from "../DataTable";
import DownloadReportLoader from "../DownloadReportLoader";
import PageHeader from "../PageHeader";
import store from "../../store";

const SummaryCard = ({ title, icon: Icon, children }) => (
  <div className="summary-card">
    <div className="summary-card__header">
      {Icon && <Icon className="summary-card__icon" />}
      <h3 className="summary-card__title">{title}</h3>
    </div>
    <div className="summary-card__content">{children}</div>
  </div>
);

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

// const OrderStats = ({ data }) => {
//   const formatCurrency = (amount) =>
//     new Intl.NumberFormat("en-IN", {
//       style: "currency",
//       currency: "INR",
//       maximumFractionDigits: 0,
//     })
//       .format(amount)
//       .replace("INR", "₹");

//   return (
//     <div className="bg-white rounded-xl flex flex-col">
//       <div className="flex-1 space-y-3">
//         {/* Order Statistics Grid */}
//         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
//           {/* Total Orders */}
//           <div className="bg-blue-50 rounded-xl p-2.5 sm:p-3 space-y-0.5">
//             <div className="flex items-center gap-1.5">
//               <div className="bg-blue-100 p-1 rounded-lg">
//                 <Users className="w-4 h-4 text-blue-600" />
//               </div>
//               <span className="text-gray-600 text-xs font-medium">
//                 Total Orders
//               </span>
//             </div>
//             <div className="text-lg sm:text-xl font-bold text-blue-600">
//               {data.totalOrders}
//             </div>
//           </div>

//           {/* Average Order Value */}
//           <div className="bg-purple-50 rounded-xl p-2.5 sm:p-3 space-y-0.5">
//             <div className="flex items-center gap-1.5">
//               <div className="bg-purple-100 p-1 rounded-lg">
//                 <Receipt className="w-4 h-4 text-purple-600" />
//               </div>
//               <span className="text-gray-600 text-xs font-medium">
//                 Avg Order Value
//               </span>
//             </div>
//             <div className="text-lg sm:text-xl font-bold text-purple-600">
//               {formatCurrency(data.avgOrderValue)}
//             </div>
//           </div>

//           {/* Total Discount */}
//           <div className="bg-emerald-50 rounded-xl p-2.5 sm:p-3 space-y-0.5">
//             <div className="flex items-center gap-1.5">
//               <div className="bg-emerald-100 p-1 rounded-lg">
//                 <Tag className="w-4 h-4 text-emerald-600" />
//               </div>
//               <span className="text-gray-600 text-xs font-medium">
//                 Total Discount
//               </span>
//             </div>
//             <div className="text-lg sm:text-xl font-bold text-emerald-600">
//               {formatCurrency(data.salesData.discount)}
//             </div>
//           </div>
//         </div>

//         {/* Tax Information */}
//         {(data.taxBreakdown.cgst > 0 || data.taxBreakdown.sgst > 0) && (
//           <div className="space-y-2 pt-2 mt-2 border-t border-gray-100">
//             <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
//               <div className="flex items-center gap-1.5">
//                 <div className="bg-blue-100 p-1 rounded-lg">
//                   <Percent className="w-4 h-4 text-blue-600" />
//                 </div>
//                 <span className="text-gray-600 text-xs font-medium">
//                   Tax Breakdown
//                 </span>
//               </div>
//               <div className="text-gray-700 text-xs font-semibold">
//                 Total: {formatCurrency(data.taxBreakdown.totalGst)}
//               </div>
//             </div>

//             <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
//               {/* CGST Box */}
//               <div className="bg-gray-50 rounded-xl p-2 border border-gray-100">
//                 <div className="text-gray-600 text-xs font-medium">CGST</div>
//                 <div className="text-lg sm:text-xl font-bold text-gray-800">
//                   {formatCurrency(data.taxBreakdown.cgst)}
//                 </div>
//               </div>

//               {/* SGST Box */}
//               <div className="bg-gray-50 rounded-xl p-2 border border-gray-100">
//                 <div className="text-gray-600 text-xs font-medium">SGST</div>
//                 <div className="text-lg sm:text-xl font-bold text-gray-800">
//                   {formatCurrency(data.taxBreakdown.sgst)}
//                 </div>
//               </div>
//             </div>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

const PAYMENT_METHODS = {
  ONLINE: "online",
  CASH: "cash",
  CARD: "card",
  OTHERS: "others",
  PENDING: "pending",
};

const PAYMENT_COLORS = {
  [PAYMENT_METHODS.ONLINE]: "#10b981", // emerald green (growth)
  [PAYMENT_METHODS.CASH]: "#facc15", // warm yellow (cash flow)
  [PAYMENT_METHODS.CARD]: "#8b5cf6", // violet (premium tech)
  [PAYMENT_METHODS.OTHERS]: "#94a3b8", // slate (neutral)
  [PAYMENT_METHODS.PENDING]: "#fb7185", // soft rose (pending action)
};

const PAYMENT_LABELS = {
  [PAYMENT_METHODS.ONLINE]: "Online",
  [PAYMENT_METHODS.CASH]: "Cash",
  [PAYMENT_METHODS.CARD]: "Card",
  [PAYMENT_METHODS.OTHERS]: "Others",
  [PAYMENT_METHODS.PENDING]: "Pending",
};

const preparePaymentSegments = (
  paymentMethods,
  finalAmount,
  formatCurrency
) => {
  if (!paymentMethods || !finalAmount) return [];

  return Object.entries(paymentMethods)
    .filter(
      ([method, value]) =>
        value > 0 &&
        PAYMENT_COLORS[method] &&
        typeof value === "number" &&
        method !== "pending"
    )
    .map(([method, value]) => ({
      label:
        PAYMENT_LABELS[method] ||
        method.charAt(0).toUpperCase() + method.slice(1),
      value,
      color: PAYMENT_COLORS[method],
      tooltipValue: formatCurrency(value),
    }))
    .sort((a, b) => b.value - a.value);
};

const calculatePeakHourOrders = (orders) => {
  if (!orders || orders.length === 0) return 0;
  const hourlyOrders = orders.reduce((acc, order) => {
    const hour = new Date(order.date).getHours();
    acc[hour] = (acc[hour] || 0) + 1;
    return acc;
  }, {});
  return Math.max(...Object.values(hourlyOrders));
};

const Overview = observer(() => {
  const { dateType, startDate, endDate } = dateStore;
  const { revenueReport } = useCloud();
  const [loading, setLoading] = useState(true);
  const [dataReady, setDataReady] = useState(false);
  const [summaryData, setSummaryData] = useState({
    totalOrders: 0,
    avgOrderValue: 0,
    salesData: { subtotal: 0, discount: 0, finalAmount: 0 },
    taxBreakdown: { cgst: 0, sgst: 0, totalGst: 0 },
    paymentMethods: { cash: 0, card: 0, online: 0, pending: 0, others: 0 },
  });
  const [dailyPerformance, setDailyPerformance] = useState([]);
  const [currentPage, setCurrentPage] = useState("1");
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const fetchTimeoutRef = useRef(null);
  const isFirstRender = useRef(true);

  // Transform daily performance data for bar chart
  const transformedDailyData = dailyPerformance.map((day) => ({
    label: new Date(day.date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
    }),
    revenue: day.salesData.finalAmount,
    orders: day.totalOrders,
    gst: day.taxBreakdown.totalGst,
  }));

  // Calculate percentage change
  const calculatePercentageChange = () => {
    if (dailyPerformance.length < 2) return 0;

    const currentValue =
      dailyPerformance[dailyPerformance.length - 1]?.salesData.finalAmount || 0;
    const previousValue =
      dailyPerformance[dailyPerformance.length - 2]?.salesData.finalAmount || 0;

    if (previousValue === 0) return 0;
    return ((currentValue - previousValue) / previousValue) * 100;
  };

  const percentageChange = calculatePercentageChange();

  // Payment method colors
  const paymentColors = {
    online: "#10b981", // emerald green (growth)
    cash: "#facc15", // warm yellow (cash flow)
    card: "#8b5cf6", // violet (premium tech)
    others: "#94a3b8", // slate (neutral)
    pending: "#fb7185", // soft rose (pending action)
  };

  // Prepare payment methods data for pie chart
  const paymentMethodsData = [
    {
      name: "Online",
      value: summaryData.paymentMethods.online,
      color: paymentColors.online,
      description:
        "Payments made through digital platforms like UPI, net banking, etc.",
    },
    {
      name: "Cash",
      value: summaryData.paymentMethods.cash,
      color: paymentColors.cash,
      description: "Physical cash payments collected at the counter",
    },
    {
      name: "Card",
      value: summaryData.paymentMethods.card,
      color: paymentColors.card,
      description: "Credit and debit card transactions",
    },
    {
      name: "Pending",
      value: summaryData.paymentMethods.pending,
      color: paymentColors.pending,
      description: "Pending payments",
    },
    {
      name: "Unsettled",
      value: summaryData.paymentMethods.unsettled,
      color: paymentColors.unsettled,
      description: "Unsettled payments",
    },
    {
      name: "Others",
      value: summaryData.paymentMethods.others,
      color: paymentColors.others,
      description:
        "Other payment methods including vouchers, loyalty points, etc.",
    },
  ].filter((method) => method.value > 0);

  const fetchRevenueSummary = async ({
    startDate = dateStore.startDate,
    endDate = dateStore.endDate,
    dateType = dateStore.dateType,
    page = currentPage,
  }) => {
    try {
      setLoading(true);
      setDataReady(false);
      const { data, status, pagination } = await revenueReport({
        dateType,
        startDate,
        endDate,
        page,
      });

      if (status !== SUCCESS) {
        return;
      }
      if (data) {
        const { total, dateWiseData } = data;
        setSummaryData(total);
        setDailyPerformance(dateWiseData);
        setTotalPages((prev) =>
          (pagination.totalPages || 1) > prev ? pagination.totalPages : prev
        );
        setCurrentPage(pagination.currentPage || 1);
        setDataReady(true);
      }
    } catch (error) {
      console.error("Error fetching revenue data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch on mount
  useEffect(() => {
    if (isFirstRender.current && startDate && endDate) {
      fetchRevenueSummary({});
      isFirstRender.current = false;
    }
  }, []);

  // Handle subsequent data fetches with debounce
  // useEffect(() => {
  //   if (!isFirstRender.current && startDate && endDate && !loading) {
  //     // Clear any existing timeout
  //     if (fetchTimeoutRef.current) {
  //       clearTimeout(fetchTimeoutRef.current);
  //     }

  //     // Set new timeout
  //     fetchTimeoutRef.current = setTimeout(() => {
  //       fetchRevenueSummary();
  //     }, 500);

  //     // Cleanup timeout on unmount or when dependencies change
  //     return () => {
  //       if (fetchTimeoutRef.current) {
  //         clearTimeout(fetchTimeoutRef.current);
  //       }
  //     };
  //   }
  // }, [startDate, endDate, currentPage]);

  // Format date range for display
  const getDateRangeDisplay = () => {
    switch (dateType) {
      case REPORT_DATE_TYPES.TODAY:
        return "Today";
      case REPORT_DATE_TYPES.YESTERDAY:
        return "Yesterday";
      case REPORT_DATE_TYPES.LAST_7_DAYS:
        return "Last 7 Days";
      case REPORT_DATE_TYPES.LAST_30_DAYS:
        return "Last 30 Days";
      case REPORT_DATE_TYPES.LAST_90_DAYS:
        return "Last 90 Days";
      case REPORT_DATE_TYPES.LAST_180_DAYS:
        return "Last 180 Days";
      default:
        return `${startDate} - ${endDate}`;
    }
  };

  const isDateWithinPastThreeMonths = (date) => {
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
    const checkDate = new Date(date);
    return checkDate >= threeMonthsAgo && checkDate <= new Date();
  };

  const handleDateChange = ({ startDate, endDate, dateType }) => {
    if (loading) return;
    setCurrentPage(1);
    setTotalPages(1);
    fetchRevenueSummary({
      startDate,
      endDate,
      dateType,
      page: 1,
    });
  };

  const handleBarClick = (data) => {
    // Extract the date from the label (assuming format "MMM dd")
    const clickedDate = new Date(data.label + ", " + new Date().getFullYear());

    // Set the date range to this specific day
    const formattedDate = formatLocalDate(clickedDate);
    dateStore.setCustomDateRange(formattedDate, formattedDate);
    fetchRevenueSummary({});
  };

  // Format dates as YYYY-MM-DD while preserving local date
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Stats data
  const stats = [
    {
      title: "Total Revenue",
      value: summaryData.salesData.finalAmount,
      prefix: "₹",
      subtitle: `Net: ₹${
        summaryData.salesData.finalAmount - summaryData.taxBreakdown.totalGst
      }`,
      icon: IndianRupee,
      variant: "default",
    },
    {
      title: "Average Order Value",
      value: summaryData.avgOrderValue,
      prefix: "₹",
      subtitle: `From ${summaryData.totalOrders} Orders`,
      icon: Receipt,
      variant: "success",
    },
    {
      title: "Peak Hour Orders",
      value: calculatePeakHourOrders(dailyPerformance),
      subtitle: "Max Orders/Hour",
      icon: Clock,
      variant: "info",
    },
    {
      title: "Discount Impact",
      value: (
        (summaryData.salesData.discount / summaryData.salesData.subtotal) *
        100
      ).toFixed(1),
      suffix: "%",
      subtitle: `₹${formatCurrency(summaryData.salesData.discount)} Total`,
      icon: Tag,
      variant: summaryData.salesData.discount > 0 ? "warning" : "success",
    },
    {
      title: "Online vs Cash",
      value: (
        (summaryData.paymentMethods.online /
          summaryData.salesData.finalAmount) *
        100
      ).toFixed(1),
      suffix: "%",
      subtitle: "Digital Payments",
      icon: Wallet,
      variant: "default",
    },
    {
      title: "Tax Contribution",
      value: (
        (summaryData.taxBreakdown.totalGst /
          summaryData.salesData.finalAmount) *
        100
      ).toFixed(1),
      suffix: "%",
      subtitle: `GST: ₹${formatCurrency(summaryData.taxBreakdown.totalGst)}`,
      icon: Percent,
      variant: "info",
    },
  ];

  if (loading || !dataReady) {
    return (
      <ReportLoadingState
        dateType={dateType}
        startDate={startDate}
        endDate={endDate}
        onDateChange={handleDateChange}
        variant="overview"
      />
    );
  }

  // Define columns configuration for DataTable
  const columns = [
    {
      key: "date",
      label: "Date",
      render: (row) =>
        new Date(row.date).toLocaleDateString("en-IN", {
          day: "numeric",
          month: "short",
        }),
    },
    {
      key: "totalOrders",
      label: "Orders",
      render: (row) => (
        <span className="table-order-badge">{row.totalOrders}</span>
      ),
    },
    {
      key: "revenue",
      label: "Revenue",
      render: (row) => formatCurrency(row.salesData.finalAmount),
    },
    {
      key: "gst",
      label: "GST Details",
      render: (row) => (
        <div className="table-gst-details">
          <div className="table-gst-row">
            CGST: {formatCurrency(row.taxBreakdown.cgst)}
          </div>
          <div className="table-gst-row">
            SGST: {formatCurrency(row.taxBreakdown.sgst)}
          </div>
          <div className="table-gst-total">
            Total: {formatCurrency(row.taxBreakdown.totalGst)}
          </div>
        </div>
      ),
    },
    {
      key: "paymentDistribution",
      label: "Payment Distribution",
      width: "250px",
      render: (row) => {
        const segments = preparePaymentSegments(
          row.paymentMethods,
          row.salesData.finalAmount,
          formatCurrency
        );

        if (segments.length === 0) {
          return (
            <div className="text-gray-500 text-sm italic px-5">
              No payment data available
            </div>
          );
        }

        return (
          <div className="w-full px-5">
            <StackedProgressBar
              segments={segments}
              total={row.salesData.finalAmount}
              height="h-2"
              className="payment-progress-bar"
            />
          </div>
        );
      },
    },
  ];

  if (!store.GST.active) {
    delete columns[3];
  }

  const handleSort = (key, direction) => {
    let sortedDate = [...dailyPerformance]; // Create a new array to avoid mutating state directly

    switch (key) {
      case "date":
        sortedDate = sortedDate.sort((a, b) => {
          // Direct string comparison for yyyy-mm-dd format
          if (direction === "asc") {
            return a.date.localeCompare(b.date);
          }
          return b.date.localeCompare(a.date);
        });
        break;
      case "totalOrders":
        sortedDate = sortedDate.sort((a, b) => {
          if (direction === "asc") {
            return a.totalOrders - b.totalOrders;
          }
          return b.totalOrders - a.totalOrders;
        });
        break;
      case "revenue":
        sortedDate = sortedDate.sort((a, b) => {
          if (direction === "asc") {
            return a.salesData.finalAmount - b.salesData.finalAmount;
          }
          return b.salesData.finalAmount - a.salesData.finalAmount;
        });
        break;
    }

    setDailyPerformance([...sortedDate]); // Set state with new array to ensure re-render
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchRevenueSummary({
      page,
    });
  };

  return (
    <div className="overview-container">
      <PageHeader
        title="Revenue Overview"
        description="Comprehensive view of your business performance and revenue analytics"
        rightSlot={
          <DateFilter
            dateType={dateType}
            startDate={startDate}
            endDate={endDate}
            onDateChange={handleDateChange}
            reportType={"revenue-report"}
          />
        }
        mobileDropdownContent={
          <DateFilter
            dateType={dateType}
            startDate={startDate}
            endDate={endDate}
            onDateChange={handleDateChange}
            reportType={"revenue-report"}
          />
        }
        className="mb-6"
      />

      <div className="overview-grid">
        {/* Revenue Chart */}
        <div className="h-[400px]">
          <GenericBarChart
            title="Revenue"
            data={transformedDailyData}
            dataKey="revenue"
            xAxisKey="label"
            barColor="#3b82f6"
            totalAmount={formatCurrency(summaryData.salesData.finalAmount)}
            dateRange={getDateRangeDisplay()}
            tooltipFormatter={(value) => formatCurrency(value)}
            tooltipTitle="Revenue"
            tooltipSubtitle="Daily Total"
            onBarClick={handleBarClick}
          />
        </div>

        {/* GST Breakdown Card */}
        <SummaryCard title="Order Statistics" icon={Receipt}>
          <OrderStats data={summaryData} />
        </SummaryCard>

        {/* Payment Methods Chart */}
        <div className="h-[400px]">
          <GenericPieChart
            title="Payment Methods"
            subtitle=""
            data={paymentMethodsData}
            dataKey="value"
            nameKey="name"
            colors={["#22c55e", "#3b82f6", "#8b5cf6"]}
            tooltipFormatter={formatCurrency}
            centerLabel={{
              value: formatCurrency(summaryData.salesData.finalAmount),
              label: "Total Revenue",
            }}
            tooltipTitle="Amount"
            showLegend={false}
            showHeader={true}
            border={true}
            innerRadius="55%"
            outerRadius="100%"
            showSummary={true}
          />
        </div>
      </div>

      {/* Daily Performance Table */}
      <DataTable
        data={dailyPerformance}
        columns={columns}
        title="Daily Performance"
        subtitle="Day-wise sales breakdown"
        formatCurrency={formatCurrency}
        totalPages={totalPages}
        currentPage={currentPage}
        onSort={handleSort}
        onPageChange={handlePageChange}
        onPrev={() => handlePageChange(currentPage - 1)}
        onNext={() => handlePageChange(currentPage + 1)}
        sortableKeys={["date", "totalOrders", "revenue"]}
      />
    </div>
  );
});

export default Overview;
